import {useQuantityFormat} from '@aboutyou/i18n';
import {styled as linariaStyled} from '@linaria/react';
import React, {FC} from 'react';
import sc from 'styled-components';
import {COLORS} from '../../../../../constants/colors';
import {SPACING} from '../../../../../constants/sizing';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../../../constants/typography';
import {Icon} from '../../../../atoms/Icon/Icon';
import {NumberBadge} from '../../../../atoms/NumberBadge/NumberBadge';
import {FilterFlyoutIconName} from '../../assets';
import {getBorderColor, getCheckmarkColor, getFlexDirection} from './helpers';
import {
  FilterColor as Color,
  ColorItemLabelPosition as LabelPosition,
  ColorItemSize as Size,
} from './types';

interface Props {
  color: Color;
  labelPosition: LabelPosition;
  onClick: (colorId: string) => void;
  selected: boolean;
  size: Size;
  showProductCount: boolean;
  className?: string;
  constrainAndCenterLabel?: boolean;
}

export const ColorItem: FC<Props> = ({
  color,
  labelPosition,
  onClick,
  selected,
  size,
  showProductCount,
  className,
  ...props
}) => {
  const {gradient, isLightColor} = color;
  const borderColor = getBorderColor({
    isActive: selected,
    isLightColor,
  });
  const checkmarkColor = getCheckmarkColor({isLightColor});
  const quantityFormat = useQuantityFormat();

  const checkboxChildren = (
    <>
      <Input
        checked={selected}
        data-testid={`filterFlyoutColorInput-${color.id}`}
        onChange={() => onClick(`${color.id}`)}
        type="checkbox"
      />
      <CheckmarkIcon
        asset={FilterFlyoutIconName.ColorCheckmarkSmall}
        color={checkmarkColor}
        data-testid="filterFlyoutColorCheckmark"
        height={size === 'small' ? 14 : 32}
        opacity={selected ? '1' : '0'}
        width={size === 'small' ? 14 : 32}
      />
    </>
  );

  return (
    <Label
      {...props}
      data-testid="filterFlyoutColorLabel"
      key={`filter-flyout-color-${color.id}`}
      labelPosition={labelPosition}
      className={className}
      data-active={selected ? 'true' : 'false'}
    >
      <BoxAndLabelWrapper>
        <Checkbox
          borderColor={borderColor}
          color={color.hex}
          colors={gradient}
          data-testid={`filterFlyoutColorCheckbox-${color.id}`}
          labelPosition={labelPosition}
          $size={size}
        >
          {checkboxChildren}
        </Checkbox>
        <LabelText data-testid="filterFlyoutColorLabelText" $size={size}>
          {[...color.label]
            .map((char, i) => (i === 0 ? char.toUpperCase() : char))
            .join('')}
        </LabelText>
      </BoxAndLabelWrapper>
      {showProductCount && (
        <StyledNumberBadge
          count={quantityFormat(color.productCount)}
          size="small"
        />
      )}
    </Label>
  );
};

const BoxAndLabelWrapper = sc.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Label = sc.label<{
  labelPosition: LabelPosition;
  $active?: boolean;
}>`
  position: relative;
  display: flex;
  flex-direction: ${({labelPosition}) => getFlexDirection(labelPosition)};
  cursor: pointer;
  justify-content: space-between;
  text-overflow: ellipsis;
  align-items: center;
  gap: 8px;
  padding:1rem;
  &:not(:first-child) {
    padding-top: ${SPACING['X.Lg']};
  }
  padding-bottom: ${SPACING['X.Lg']};
  &[data-active='true'] {
    background-color: ${COLORS['Fill.Grey.Primary']};
  }

`;

const exactGradient = (colors: Array<string>) =>
  `linear-gradient(45deg, ${colors
    .map((color: string, i: number) => `${color} ${i * (100 / colors.length)}%`)
    .join(', ')})`;
const simpleGradient =
  'conic-gradient(rgb(255, 152, 0) 25%, rgb(76, 175, 80) 0 50%, rgb(244, 67, 54) 0 75%, rgb(33, 150, 243) 0)';
const gradient = (size: Size, colors: Array<string>) =>
  size === 'small' ? simpleGradient : exactGradient(colors);

const Checkbox = sc.div<{
  borderColor?: string;
  color?: string;
  colors?: Array<string>;
  labelPosition: LabelPosition;
  $size: Size;
}>`
  box-sizing: border-box;
  position: relative;
  width: ${({$size}) => ($size === 'small' ? '1.375rem' : '2.14rem')};
  height: ${({$size}) => ($size === 'small' ? '1.375rem' : '2rem')};
  margin-top: ${({labelPosition}) =>
    labelPosition === 'top' ? '0.325rem' : '0'};
  margin-right: ${({labelPosition}) =>
    labelPosition === 'right' ? '0.75rem' : '0'};
  margin-left: ${({labelPosition}) =>
    labelPosition === 'left' ? '0.75rem' : '0'};
  border-radius: 3px;
  border: ${({borderColor}) =>
    borderColor ? `1px solid ${borderColor}` : COLORS.White};
  background: ${({color, colors, $size}) =>
    colors?.length ? gradient($size, colors) : color};
`;

const Input = sc.input`
  position: absolute;
  top: 0;
  left: 0;
  opacity: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  cursor: pointer;
`;

const LabelText = sc.div<{
  $size: Size;
}>`
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.mediumBold};
  color: ${COLORS.Black};
  line-height: 1.42;
`;

const CheckmarkIcon = linariaStyled(Icon)<{color: string; opacity: string}>`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  fill: ${({color}) => color};
  opacity: ${({opacity}) => opacity};
  pointer-events: none;
  transition: opacity 0.15s;
`;

const StyledNumberBadge = sc(NumberBadge)`
  margin-left: 5px;
  border: 1px solid ${COLORS['Outline.Grey']};
`;
